import { FEE_APPLIES_TO, STUDENT_TYPE_OPTIONS } from './feeStudentType';

/** Tenant campus student mix — set by super admin at provision time. */
export type StudentResidenceMode = 'day_scholar' | 'resident' | 'both';

export type StudentResidenceCapabilities = {
  mode: StudentResidenceMode;
  allowsDayScholar: boolean;
  allowsResident: boolean;
};

export const STUDENT_RESIDENCE_MODE_OPTIONS: Array<{
  value: StudentResidenceMode;
  title: string;
  body: string;
}> = [
  {
    value: 'day_scholar',
    title: 'Day scholar only',
    body: 'No hostel. Student types and pickup/transport stay day-scholar focused.',
  },
  {
    value: 'resident',
    title: 'Resident only',
    body: 'Hostel campus. Day-scholar types, bus opt-in, and day-scholar fee scopes are hidden.',
  },
  {
    value: 'both',
    title: 'Day scholar + Resident',
    body: 'Mixed campus — both hostel and day-scholar features stay available.',
  },
];

/** Existing tenants without the field behave as mixed campuses. */
export function normalizeStudentResidenceMode(value: unknown): StudentResidenceMode {
  const raw = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  if (
    raw === 'day_scholar' ||
    raw === 'dayscholar' ||
    raw === 'day' ||
    raw === 'visiting' ||
    raw === 'day_only'
  ) {
    return 'day_scholar';
  }
  if (
    raw === 'resident' ||
    raw === 'hostel' ||
    raw === 'boarding' ||
    raw === 'resident_only' ||
    raw === 'hostel_only'
  ) {
    return 'resident';
  }
  return 'both';
}

export function studentResidenceModeLabel(value: unknown): string {
  switch (normalizeStudentResidenceMode(value)) {
    case 'day_scholar':
      return 'Day scholar only';
    case 'resident':
      return 'Resident only';
    default:
      return 'Day scholar + Resident';
  }
}

export function capabilitiesFromResidenceMode(
  value: unknown
): StudentResidenceCapabilities {
  const mode = normalizeStudentResidenceMode(value);
  return {
    mode,
    allowsDayScholar: mode === 'day_scholar' || mode === 'both',
    allowsResident: mode === 'resident' || mode === 'both',
  };
}

export function defaultStudentTypeForResidenceMode(value: unknown): string {
  const caps = capabilitiesFromResidenceMode(value);
  if (!caps.allowsDayScholar && caps.allowsResident) return 'resident';
  return 'regular';
}

export function studentTypeOptionsForResidenceMode(value: unknown) {
  const caps = capabilitiesFromResidenceMode(value);
  return STUDENT_TYPE_OPTIONS.filter((option) => {
    if (option.value === 'resident') return caps.allowsResident;
    return caps.allowsDayScholar;
  });
}

export function feeAppliesToOptionsForResidenceMode(value: unknown) {
  const caps = capabilitiesFromResidenceMode(value);
  return FEE_APPLIES_TO.filter((option) => {
    if (option.value === 'all') return true;
    if (option.value === 'resident') return caps.allowsResident;
    if (option.value === 'visiting') return caps.allowsDayScholar;
    return true;
  });
}

export function isStudentTypeAllowedForResidenceMode(
  studentType: string | null | undefined,
  residenceMode: unknown
): boolean {
  const key = (studentType ?? '').trim().toLowerCase();
  if (!key) return true;
  const caps = capabilitiesFromResidenceMode(residenceMode);
  if (key === 'resident') return caps.allowsResident;
  if (key === 'regular' || key === 'visiting' || key === 'day_scholar' || key === 'day-scholar') {
    return caps.allowsDayScholar;
  }
  return true;
}

/** Module codes forced on/off when super admin sets residence mode. */
export function residenceModeModuleUpdates(
  value: unknown
): Array<{ module_code: string; enabled: boolean }> {
  const caps = capabilitiesFromResidenceMode(value);
  const updates: Array<{ module_code: string; enabled: boolean }> = [
    { module_code: 'hostel', enabled: caps.allowsResident },
  ];
  if (!caps.allowsDayScholar) {
    updates.push({ module_code: 'transport', enabled: false });
  }
  return updates;
}

/** Nav / subscription codes that must stay hidden for the active residence mode. */
export function isModuleHiddenByResidenceMode(
  moduleCode: string | null | undefined,
  residenceMode: unknown
): boolean {
  const code = (moduleCode ?? '').trim().toLowerCase();
  if (!code) return false;
  const caps = capabilitiesFromResidenceMode(residenceMode);
  if (code === 'hostel' && !caps.allowsResident) return true;
  if (code === 'transport' && !caps.allowsDayScholar) return true;
  return false;
}
